import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { StatusBadge, AgentDraftBadge } from "../components/Brand";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ReviewQueue() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/review-queue").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="review-queue-loading">Loading queue…</div>;

  const act = async (path, id, msg) => {
    setBusyId(id);
    try { await api.post(path); await load(); toast.success(msg); }
    catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  return (
    <div className="max-w-6xl space-y-8" data-testid="review-queue-page">
      <div className="fade-up">
        <p className="eyebrow">Admin</p>
        <h1 className="section-title text-3xl mt-1">Review & Approval Queue</h1>
        <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
          Approving a record or draft confirms a human reviewed it. Approval never sends anything —
          external email remains disabled until you activate it in Settings.
        </p>
      </div>

      <div className="card p-6 fade-up-1" data-testid="queue-logs-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-4">Logs awaiting review ({data.logs.length})</h2>
        {data.logs.length === 0 && <p className="text-sm text-[#78716C]" data-testid="queue-logs-empty">Nothing waiting. Queue is clear.</p>}
        <ul className="divide-y divide-[#F3EFE6]">
          {data.logs.map((lg) => (
            <li key={lg.id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`queue-log-${lg.id}`}>
              <div>
                <Link to={`/logs/${lg.id}`} className="font-bold text-navy hover:text-terracotta" data-testid={`queue-log-link-${lg.id}`}>
                  {lg.school_name} · {lg.service_type} · {lg.date}
                </Link>
                <div className="text-xs text-[#78716C] mt-0.5">{lg.consultant_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={lg.status} />
                {lg.status === "submitted" && (
                  <button className="btn-outline !py-1.5 !px-4" disabled={busyId === lg.id}
                    onClick={() => act(`/logs/${lg.id}/review`, lg.id, "Marked reviewed.")} data-testid={`queue-review-${lg.id}`}>
                    Mark reviewed</button>
                )}
                <button className="btn-primary !py-1.5 !px-4" disabled={busyId === lg.id}
                  onClick={() => act(`/logs/${lg.id}/approve`, lg.id, "Record approved.")} data-testid={`queue-approve-${lg.id}`}>
                  <CheckCircle2 size={14} /> Approve</button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="card p-6 fade-up-2" data-testid="queue-drafts-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-4">Follow-up drafts pending approval ({data.drafts.length})</h2>
        {data.drafts.length === 0 && <p className="text-sm text-[#78716C]" data-testid="queue-drafts-empty">No pending drafts.</p>}
        <ul className="divide-y divide-[#F3EFE6]">
          {data.drafts.map((d) => (
            <li key={d.id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`queue-draft-${d.id}`}>
              <div className="min-w-0">
                <Link to={`/logs/${d.daily_log_id}`} className="font-bold text-navy hover:text-terracotta" data-testid={`queue-draft-link-${d.id}`}>
                  {d.subject || "(no subject)"}
                </Link>
                <div className="text-xs text-[#78716C] mt-0.5">
                  {d.audience} · {d.school_name} · {d.log_date} · by {d.created_by_name}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <AgentDraftBadge />
                <StatusBadge status={d.sent_status} prefix="Sent: " />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
